import { HashLink } from "react-router-hash-link"
import { NavigationItem } from "../../types"
import { ImageType } from "../../types/mainModulTypes"
import { API } from "../../constants/constants"
import { useAccessibility } from "../../context/AccessibilityContext"
import { ReactComponent as MENU } from "../../assets/Menu.svg"
import { ReactComponent as ARROWDOWN } from "../../assets/ChevronDown.svg"
import "../../style/dropdown.css"
import { useState } from "react"
import { motion } from "framer-motion"
import { createPortal } from "react-dom"
import { ReactComponent as CHEVRONRIGHT } from "../../assets/ChevronRight.svg"

type HeaderType = {
    pages: NavigationItem[]
    content: {
        navigationIcon: ImageType
        navigationLogo: ImageType
    }
}

export default function Header(props: HeaderType) {
    return (
        <div className="fixed top-0 flex justify-center items-center w-full bg-accent h-24 z-50">
            {/* DESKTOP */}
            <div className="hidden lg:flex gap-4 max-w-page w-full justify-between items-center">
                {props.content?.navigationLogo && (
                    <img
                        className="h-20 w-fit max-w-[8rem] object-contain"
                        src={API + props.content.navigationLogo.url}
                        alt={props.content.navigationLogo.description ?? props.content.navigationLogo.name ?? "logo_in_header"}
                    />
                )}
                {props.pages?.map((item) => (
                    <NavbarComponent key={item.id} item={item} isNested={false} />
                ))}

                {props.content?.navigationIcon && (
                    <img
                        className="h-16 w-fit max-w-[5rem] object-contain"
                        src={API + props.content.navigationIcon.url}
                        alt={props.content.navigationIcon.description ?? props.content.navigationIcon.name ?? "icon_in_header"}
                    />
                )}
            </div>
            {/* MOBILE */}
            <div className="drawer z-50 lg:hidden">
                <input id="my-drawer" type="checkbox" className="drawer-toggle"></input>
                <div className="drawer-content">
                    {/* Page content here */}
                    <div className="flex lg:hidden w-full justify-between items-center px-4">
                        {props.content?.navigationLogo && (
                            <img
                                className="h-16 w-fit max-w-[5rem] object-contain"
                                src={API + props.content.navigationLogo.url}
                                alt={props.content.navigationLogo.description ?? props.content.navigationLogo.name ?? "logo_in_header"}
                            />
                        )}
                        <label htmlFor="my-drawer">
                            <MENU className="w-10 h-10 text-neutral" />
                        </label>
                    </div>
                </div>
                <div className="drawer-side z-50">
                    <label htmlFor="my-drawer" aria-label="close sidebar" className="drawer-overlay"></label>
                    <div className="menu bg-base-200 text-base-content min-h-full w-80 p-4">
                        {props.pages?.map((item, index) => (
                            <NavbarElement key={index} label={item.title} link={item.url} childItems={item.children} />
                        ))}
                    </div>
                </div>
            </div>
        </div>
    )
}

type NavbarElementType = {
    label: string
    link: string
    childItems?: any[]
}

function NavbarElement(props: NavbarElementType) {
    const { getCursorPointerClass } = useAccessibility()
    const [showChilds, setshowChilds] = useState<boolean>(false)
    return (
        <div className="border-b lg:border-none z-50">
            <div className="w-full h-fit flex flex-col">
                <div
                    className={`py-4 lg:py-8 text-black text-base lg:text-xs font-semibold h-full flex flex-row items-center justify-between w-full ${
                        showChilds ? "border-b border-black" : ""
                    }`}>
                    <motion.div onHoverStart={() => setshowChilds(true)} onHoverEnd={() => setshowChilds(false)}>
                        <HashLink className={`hover:underline ${getCursorPointerClass()}`} to={props.link}>
                            {props.label}
                        </HashLink>
                    </motion.div>

                    {props.childItems && props.childItems?.length > 0 && (
                        <motion.div
                            initial={{ rotate: 0 }}
                            animate={{ rotate: showChilds ? 180 : 0 }}
                            transition={{ duration: 0.2 }}
                            className="lg:hidden">
                            <ARROWDOWN className="w-6 h-6 text-neutral" onClick={() => setshowChilds(!showChilds)} />
                        </motion.div>
                    )}
                </div>
                {showChilds && (
                    <div className="lg:hidden w-full">
                        {props.childItems?.map((item) => (
                            <NavbarElement key={item.id} label={item.title} link={item.url} childItems={item.children} />
                        ))}
                    </div>
                )}
            </div>
        </div>
    )
}

type NavbarComponentType = {
    item: NavigationItem
    isNested: boolean
}

function NavbarComponent(props: NavbarComponentType) {
    const { getCursorPointerClass } = useAccessibility()
    const [showChilds, setshowChilds] = useState<boolean>(false)
    const handleShowChilds = (bool: boolean) => {
        if (props.item.children && props.item.children.length > 0) {
            setshowChilds(bool)
        }
    }

    return (
        <div className="relative">
            <div className="py-4 lg:py-8 text-black text-base lg:text-xs font-semibold h-full flex flex-row items-center justify-between w-full">
                <motion.div onHoverStart={() => handleShowChilds(true)} onHoverEnd={() => handleShowChilds(false)}>
                    <HashLink className={`hover:underline ${getCursorPointerClass()}`} to={props.item.url}>
                        {props.item.title}
                    </HashLink>
                </motion.div>
            </div>
            {showChilds && (
                <motion.div
                    onHoverStart={() => handleShowChilds(true)}
                    onHoverEnd={() => handleShowChilds(false)}
                    id="parentDropDownContainer"
                    className="absolute top-12 left-0 min-w-[14rem] w-fit z-20">
                    <div className="max-h-[20rem] overflow-y-auto bg-white pt-4 pb-4 text-xs text-black font-semibold flex flex-col rounded-b-default px-2">
                        <div className="border-t border-gray-400 w-full pb-4" />
                        {props.item.children?.map((child, index) => (
                            <SubDropdownComponent key={index} item={child} />
                        ))}
                    </div>
                </motion.div>
            )}
        </div>
    )
}

type SubDropdownComponentType = {
    item: NavigationItem
}

function SubDropdownComponent(props: SubDropdownComponentType) {
    const { getCursorPointerClass } = useAccessibility()
    const [showChilds, setshowChilds] = useState<boolean>(false)
    const handleShowChilds = (bool: boolean) => {
        if (props.item.children && props.item.children.length > 0) {
            setshowChilds(bool)
        }
    }

    return (
        <div className="w-full relative z-30 overflow-visible ">
            <motion.div
                className={`hover:underline ${getCursorPointerClass()} cursor-pointer py-1 mb-1 w-full px-4 border rounded-full text-center`}
                onHoverStart={() => handleShowChilds(true)}
                onHoverEnd={() => handleShowChilds(false)}>
                <HashLink className="flex relative justify-center items-center" to={props.item.url}>
                    {props.item.title}
                    {props.item.children.length > 0 && <CHEVRONRIGHT className="w-4 h-4 text-black absolute right-0" />}
                </HashLink>
            </motion.div>
            {showChilds &&
                createPortal(
                    <motion.div
                        onHoverStart={() => {
                            handleShowChilds(true)
                        }}
                        onHoverEnd={() => handleShowChilds(false)}
                        className="absolute top-0 left-[calc(100%-1rem)] w-full h-full max-h-[20rem] px-4 pt-4 pb-4 flex bg-white border-l flex-col text-xs text-black font-semibold overflow-y-scroll z-30">
                        <div className="border-t border-gray-400 w-full pb-4" />
                        {props.item.children?.map((child, index) => (
                            <SubDropdownComponent key={index} item={child} />
                        ))}
                    </motion.div>,
                    document.getElementById("parentDropDownContainer") as HTMLElement
                )}
        </div>
    )
}
